type ExtractRouteParams<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
	? { [k in Param | keyof ExtractRouteParams<Rest>]: string | number }
	: T extends `${string}:${infer Param}`
		? { [k in Param]: string | number }
		: Record<string, never>;

export function defineRoute<
	TPath extends string,
	TPathParams extends Record<string, any> = ExtractRouteParams<TPath>,
	TQueryParams extends Record<string, any> = Record<string, never>,
>(path: TPath) {
	return {
		path,
		build: (params: TPathParams, query?: TQueryParams): string => {
			const filledPath = Object.entries(params).reduce(
				(acc, [key, val]) => acc.replace(`:${key}`, String(val)),
				path as string,
			);

			if (!query || Object.keys(query).length === 0) return filledPath;

			const searchParams = new URLSearchParams();
			for (const [key, val] of Object.entries(query)) {
				if (val === undefined || val === null) continue;
				// Array of objects → bracket-dot syntax (e.g. filter[0].id, filter[0].value)
				// matching what the table URL parser reads back via useUrlQuerySync.
				if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
					val.forEach((item, index) => {
						for (const [prop, propVal] of Object.entries(item as Record<string, unknown>)) {
							if (propVal === undefined || propVal === null) continue;
							searchParams.set(`${key}[${index}].${prop}`, String(propVal));
						}
					});
					continue;
				}
				searchParams.set(key, String(val));
			}

			return `${filledPath}?${searchParams.toString()}`;
		},
	};
}
